import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Loader, X } from "lucide-react";
import { useSelector } from "react-redux";
import { AppState } from "../../state";
import {
  useReviewClass,
  useSwitchPage,
} from "../../state/features/applicationSlice/hooks";
import { ApplicationPages } from "../../state/features/applicationSlice";
import { toJSDate } from "../../functions";
import Star from "../../components/icons/star";
import { StepTwo } from "../../components/BookingSteps/StepTwo";
import { toast } from "react-toastify";
import { StepOne } from "../../components/BookingSteps/StepOne";
import { useUserAuth } from "../../state/features/authSlice/hooks";
import { CountryType } from "../../constants/countries";
import {
  WellnessClassSubscription,
  WellnessClass,
} from "../../types/HealthInstitution";
import { getWellnessClassSubscriptions } from "../../functions/firebase";
import { format } from "date-fns";

interface ReviewProps {}

const RATING_KEYS = ["Terrible", "Bad", "Okay", "Good", "Great"] as const;

const Review: React.FC<ReviewProps> = () => {
  const { User, Account } = useSelector<AppState, AppState["auth"]>(
    (s) => s.auth
  );

  const { activeFacility, institutionClasses } = useSelector<
    AppState,
    AppState["application"]
  >((state) => state.application);

  const router = useSwitchPage();
  const reviewClass = useReviewClass();

  const [isLoading, setIsLoading] = useState(false);
  const [newRating, setNewRating] = useState({
    rating: 0,
    feedback: "",
    custom: false,
  });
  const [classSubs, setClassSubs] = useState<WellnessClassSubscription[]>([]);
  // const [activeIndex, setActiveIndex] = useState<number>(0);

  const activeClassAndSubscription = useMemo(() => {
    const activeSubscription = classSubs.length > 0 ? classSubs[0] : null;
    const activeClass = activeSubscription
      ? institutionClasses.find(
          (wellnessClass) => wellnessClass.id === activeSubscription.classId
        )
      : null;
    return {
      activeSubscription,
      activeClass,
    };
  }, [classSubs, institutionClasses]);

  useEffect(() => {
    if (!Account || !activeFacility) return;
    const unsub = getWellnessClassSubscriptions(
      Account.uid,
      Account.primary_member_id,
      activeFacility.id,
      (subscriptions: WellnessClassSubscription[] = []) => {
        const sorted = subscriptions.sort((a, b) => {
          const aDt = toJSDate(a.startTime).getTime();
          const bDt = toJSDate(b.startTime).getTime();
          return aDt - bDt;
        });
        setClassSubs(sorted);
        // setActiveIndex(0);
      }
    );

    return () => {
      unsub();
    };
  }, [Account]);
  console.log("activeClassAndSubscription", activeClassAndSubscription);
  // submit review for current active classSubscription
  const handleSubmit = useCallback(async () => {
    if (!activeClassAndSubscription.activeSubscription) {
      toast.warning("No class booking selected");
      return;
    }
    if (!User) {
      toast.warning("You have to be signed in to leave a review");
      return;
    }
    if (newRating.rating === 0) {
      toast.warning("Please pick a rating");
      return;
    }

    setIsLoading(true);
    try {
      await reviewClass({
        reviewData: {
          rating: newRating.rating,
          bookedClassId: activeClassAndSubscription.activeSubscription.id,
          facilityId: activeClassAndSubscription.activeSubscription.facilityId,
          feedback: newRating.feedback,
          patientData: {
            email: User.email,
            id: User.accountId,
            memberId: User.memberID,
            name: `${User.firstName} ${User.lastName}`,
          },
        },
        skipReview: false,
      });
      toast.success("Review submitted");
      if (classSubs.length > 0) {
        setNewRating({
          rating: 0,
          feedback: "",
          custom: false,
        });
      } else {
        toast.info("All reviews complete");
      }
    } catch (err) {
      console.error("Error submitting class review", err);
      toast.error("Could not submit review");
    } finally {
      setIsLoading(false);
    }
  }, [
    activeClassAndSubscription.activeSubscription,
    User,
    newRating.rating,
    newRating.feedback,
    reviewClass,
    classSubs.length,
  ]);

  const [activeStep, setActiveStep] = useState(0);
  const [phone, setPhone] = useState<string>("");
  const { loading, createPatient } = useUserAuth();
  const [countryCode, setCountryCode] = useState<CountryType>({
    code: "GH",
    label: "Ghana",
    phone: "+233",
  });
  const phoneNumber = useMemo(() => {
    return (
      countryCode.phone + parseInt(phone.replaceAll(" ", ""), 10).toString()
    );
  }, [countryCode.phone, phone]);
  const REGISTERATION_STEPS = [
    {
      title: "Step 1",
      subTitle: "Verify Account",
      component: (
        <StepOne
          title="You need to be signed in to leave a review"
          countryCode={countryCode}
          phoneNumber={phoneNumber}
          phone={phone}
          setCountryCode={setCountryCode}
          setPhoneNumber={setPhone}
          callbackFunc={() => setActiveStep((s) => s + 1)}
        />
      ),
    },
    {
      title: "Step 2",
      subTitle: "Basic Details",
      component: (
        <StepTwo
          loading={loading}
          createAccount={async (data) => {
            createPatient(
              { ...data, phoneNumber, quickSignIn: !data.password },
              () => {
                toast.success("Welcome 👍");
              }
            );
          }}
        />
      ),
    },
  ];

  const shareText =
    "Just had the most incredible wellness session at Honey Pot Spa! ✨ My mind is calm, my body feels amazing, and my soul is recharged. Ready to start your own wellness journey? Book your transformative session through DrDoGood - your future self will thank you! 💆‍♀️🧘‍♂️ #WellnessJourney #SelfCare";
  const shareUrl = "https://drdogood.com/book"; // Replace with actual booking link

const shareOn = ( platform: "twitter" | "instagram" | "tiktok" | "whatsapp" | 'facebook') => {
    const encodedText = encodeURIComponent(shareText);
    const encodedUrl = encodeURIComponent(shareUrl);
    
    let shareLink = '';
    
    switch(platform) {
      case 'twitter':
        shareLink = `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`;
        break;
      case 'facebook':
        shareLink = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`;
        break;
      case 'instagram':
        copyToClipboard();
        // Try to open Instagram app
        setTimeout(() => {
          window.location.href = 'instagram://';
          // Fallback to web version after a short delay if app doesn't open
          setTimeout(() => {
            window.open('https://www.instagram.com/', '_blank');
          }, 1500);
        }, 500);
        return;
      case 'tiktok':
        copyToClipboard();
        // Try to open TikTok app
        setTimeout(() => {
          window.location.href = 'tiktok://';
          // Fallback to web version after a short delay if app doesn't open
          setTimeout(() => {
            window.open('https://www.tiktok.com/', '_blank');
          }, 1500);
        }, 500);
        return;
      case 'whatsapp':
        shareLink = `https://wa.me/?text=${encodedText}%20${encodedUrl}`;
        break;
    }
    
    if (shareLink) {
      window.open(shareLink, '_blank');
    }
  };
  const copyToClipboard = async () => {
    try {
      const fullText = `${shareText} ${shareUrl}`;
      await navigator.clipboard.writeText(fullText);
    } catch (err) {
      alert("Could not copy to clipboard. Please copy manually.");
    }
  };
  return (
    <>
      {!Account ? (
        <div className="flex items-center justify-center w-full h-screen ">
          <div className="border border-gray-300 rounded-lg p-3 max-w-[500px]">
            <form onSubmit={(e) => e.preventDefault()} className="px-2 my-4">
              {REGISTERATION_STEPS[activeStep].component}
            </form>
          </div>
        </div>
      ) : classSubs.length === 0 ? (
        // Finished / Share view
        <div className="relative flex flex-col flex-1 h-full text-left">
          <button
            onClick={() => {
              router(ApplicationPages.HOME);
              window.location.replace(
                "/" + window.location.pathname.split("/")[1]
              );
            }}
            className="absolute p-2 bg-gray-500 rounded-full right-5 top-4 bg-opacity-30"
          >
            <X size={14} color="white" />
          </button>

          <div className="flex items-center justify-center w-full h-screen ">
            <div className="sm:border border-gray-300 rounded-lg p-8 max-w-[500px] text-center">
              <h2 className="mb-2 text-lg font-bold">
                You're done reviewing your wellness classes 🎉
              </h2>
              <p className="mb-4 text-sm text-gray-600">
                Thanks for taking the time to share your experience. Would you
                like to share with others?
              </p>

              <div className="flex flex-col gap-3">
                <div className="grid grid-cols-2 gap-3 mb-6">
                  <button
                    onClick={() => shareOn("twitter")}
                    className="flex items-center justify-center gap-2 py-3.5 px-4 bg-blue-500 hover:bg-blue-600 text-white rounded-xl font-semibold text-sm transition-all hover:-translate-y-0.5 hover:shadow-lg"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
                    </svg>
                    Twitter
                  </button>
    <button 
            onClick={() => shareOn('facebook')}
            className="flex items-center justify-center gap-2 py-3.5 px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold text-sm transition-all hover:-translate-y-0.5 hover:shadow-lg"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
            </svg>
            Facebook
          </button>
                  <button
                    onClick={() => shareOn("instagram")}
                    className="flex items-center justify-center gap-2 py-3.5 px-4 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white rounded-xl font-semibold text-sm transition-all hover:-translate-y-0.5 hover:shadow-lg"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                    </svg>
                    Instagram
                  </button>

                  <button
                    onClick={() => shareOn("tiktok")}
                    className="flex items-center justify-center gap-2 py-3.5 px-4 bg-black hover:bg-gray-800 text-white rounded-xl font-semibold text-sm transition-all hover:-translate-y-0.5 hover:shadow-lg"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
                    </svg>
                    TikTok
                  </button>

                  <button
                    onClick={() => shareOn("whatsapp")}
                    className="flex items-center justify-center gap-2 py-3.5 px-4 bg-green-500 hover:bg-green-600 text-white rounded-xl font-semibold text-sm transition-all hover:-translate-y-0.5 hover:shadow-lg"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.085" />
                    </svg>
                    WhatsApp
                  </button>

                  <button
                    onClick={copyToClipboard}
                    className={`flex items-center justify-center gap-2 py-3.5 px-4 rounded-xl font-semibold text-sm transition-all hover:-translate-y-0.5 hover:shadow-lg ${
                      "bg-indigo-500 hover:bg-indigo-600 text-white"
                    }`}
                  >
                      <>
                        <svg
                          className="w-4 h-4"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" />
                        </svg>
                        Copy Share Text
                      </>

                  </button>
                </div>
                <button
                  onClick={() => {
                    router(ApplicationPages.HOME);
                    window.location.replace(
                      "/" + window.location.pathname.split("/")[1]
                    );
                  }}
                  className="py-3 font-medium text-gray-800 border border-gray-300 rounded-lg"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        // Main review flow
        <div className="relative flex flex-col flex-1 h-full text-left">
          <button
            onClick={() => {
              router(ApplicationPages.HOME);
              window.location.replace(
                "/" + window.location.pathname.split("/")[1]
              );
            }}
            className="absolute p-2 bg-gray-500 rounded-full right-5 top-4 bg-opacity-30"
          >
            <X size={14} color="white" />
          </button>

          <div className="flex items-center justify-center w-full h-screen ">
            <div className="sm:border border-gray-300 rounded-lg p-8 max-w-[500px]">
              <div className="flex flex-col flex-1 space-y-6 text-center">
                <div>
                  <p className="text-xs text-primary">
                    Thanks for your patronage 🎉
                  </p>
                </div>

                <div className="flex items-center justify-center">
                  <div className="flex items-center justify-center w-24 h-24 overflow-hidden rounded-full bg-primary">
                    <img
                      src={activeFacility?.details?.logoImage}
                      className="w-full h-full "
                    />
                  </div>
                </div>

                {activeClassAndSubscription.activeClass &&
                  activeClassAndSubscription.activeSubscription && (
                    <div className="space-y-2 text-left">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-muted">Class</p>
                          <p className="font-medium">
                            {activeClassAndSubscription.activeClass.title}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center justify-between text-sm">
                        <div>
                          <p className="text-sm text-muted">Time</p>
                          <p className="font-medium">
                            {format(
                              toJSDate(
                                activeClassAndSubscription.activeSubscription
                                  .startTime
                              ),
                              "do MMM, yyyy - hh:mm aa"
                            )}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-muted">Price</p>
                          <p className="font-medium">
                            {activeClassAndSubscription.activeSubscription
                              .amountPaid
                              ? `GHC ${activeClassAndSubscription.activeSubscription.amountPaid}`
                              : "Free"}
                          </p>
                        </div>
                      </div>

                      {activeClassAndSubscription.activeClass?.instructor && (
                        <div className="">
                          <p className="text-sm text-muted">Instructor</p>
                          <p className="font-medium">
                            {
                              activeFacility?.classInstructors?.find(
                                (item) =>
                                  item?.id ===
                                  activeClassAndSubscription.activeClass
                                    ?.instructor
                              )?.name
                            }
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                <div className="flex flex-col items-center justify-center gap-1">
                  <h4 className="text-lg font-bold text-gray-900 ">
                    Rate your experience at{" "}
                    {activeFacility?.details?.institutionName}
                  </h4>
                </div>

                <div>
                  <div className="flex items-center justify-center gap-3 ">
                    {RATING_KEYS.map((key, idx) => {
                      return (
                        <button
                          key={key}
                          onClick={() => {
                            setNewRating({ ...newRating, rating: idx + 1 });
                          }}
                          className={`${
                            newRating.rating >= idx + 1
                              ? "text-[#ffaa00]"
                              : " text-primary/90"
                          }  flex flex-col items-center cursor-pointer`}
                          type="button"
                        >
                          <Star />
                          <span className={` text-xs`}>{key}</span>
                        </button>
                      );
                    })}
                  </div>

                  {Boolean(newRating.rating) && (
                    <div className="pt-5 text-left ">
                      {newRating.rating < 3 ? (
                        <div>
                          <p className="text-sm font-semibold text-dark">
                            Sorry to hear That.
                          </p>
                          <p className="pt-1 text-xs text-gray-500">
                            What went wrong?
                          </p>
                        </div>
                      ) : (
                        <div>
                          <p className="py-2 text-sm font-semibold text-dark">
                            Give a compliment?
                          </p>
                        </div>
                      )}
                      <textarea
                        className="w-full p-2 mt-1 text-sm border border-gray-300 rounded resize-none "
                        value={newRating.feedback}
                        onChange={(e) =>
                          setNewRating({
                            ...newRating,
                            feedback: e.target.value,
                          })
                        }
                        rows={4}
                      />
                    </div>
                  )}
                </div>

                <button
                  onClick={async () => {
                    await handleSubmit();
                  }}
                  className={`flex items-center justify-center w-full py-3 text-sm font-medium text-center text-white rounded-lg ${
                    Boolean(newRating.rating)
                      ? "bg-primary hover:bg-primary/90"
                      : "bg-gray-500"
                  }`}
                  type="button"
                  disabled={isLoading || !Boolean(newRating.rating)}
                >
                  {isLoading ? (
                    <Loader className=" animate-spin" />
                  ) : (
                    "Submit Feedback"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Review;
